import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from '../../hooks/useTranslation';
import { useLocation } from 'react-router-dom';

export default function SEOHead({ 
  title, 
  description, 
  keywords,
  image = '/images/me/F2.jpg',
  type = 'website'
}) {
  const { currentLanguage, t } = useTranslation();
  const location = useLocation();
  
  const baseUrl = 'https://stressfrei-hypnose.com';
  const currentPath = location.pathname;
  
  // Формируем canonical URL
  const canonicalUrl = `${baseUrl}${currentPath}`;
  
  // Формируем альтернативные URL для языков
  const getAlternateUrl = (lang) => {
    if (lang === 'de') {
      // Немецкий - убираем /ru из пути
      return `${baseUrl}${currentPath.replace('/ru', '')}` || baseUrl;
    } else {
      // Русский - добавляем /ru
      if (currentPath.startsWith('/ru')) {
        return `${baseUrl}${currentPath}`;
      }
      return `${baseUrl}/ru${currentPath === '/' ? '' : currentPath}`;
    }
  };
  
  const deUrl = getAlternateUrl('de');
  const ruUrl = getAlternateUrl('ru');
  
  // Используем переводы или переданные значения
  const finalTitle = title || t('seo.title');
  const finalDescription = description || t('seo.description');
  const finalKeywords = keywords || t('seo.keywords');
  
  const fullTitle = `${finalTitle} - Alfira Herrmann`;
  const fullImageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;
  
  return (
    <Helmet>
      {/* Основные meta теги */}
      <html lang={currentLanguage} />
      <title>{fullTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content="Alfira Herrmann - Zertifizierte Hypnose-Expertin" />
      
      {/* Robots */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Alternate языки */}
      <link rel="alternate" hreflang="de" href={deUrl} />
      <link rel="alternate" hreflang="ru" href={ruUrl} />
      <link rel="alternate" hreflang="x-default" href={deUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:locale" content={currentLanguage === 'de' ? 'de_DE' : 'ru_RU'} />
      <meta property="og:locale:alternate" content={currentLanguage === 'de' ? 'ru_RU' : 'de_DE'} />
      <meta property="og:site_name" content="Alfira Herrmann - Hypnotherapie" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={fullImageUrl} />
      
      {/* Geo tags */}
      <meta name="geo.region" content="DE-BW" />
      <meta name="geo.placename" content="Emmendingen" />
      <meta name="geo.position" content="48.1204;7.8518" />
      <meta name="ICBM" content="48.1204, 7.8518" />
      
      {/* Schema.org для Google */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Alfira Herrmann - Hypnotherapie",
          "image": fullImageUrl,
          "description": finalDescription,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Hochburger Str. 32",
            "addressLocality": "Emmendingen",
            "postalCode": "79312",
            "addressCountry": "DE"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 48.1204,
            "longitude": 7.8518
          },
          "url": baseUrl,
          "telephone": "+49 (0) 07641 95 37 645",
          "email": "alphavita@gmx.de",
          "priceRange": "$$",
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday"
            ],
            "opens": "09:00",
            "closes": "18:00"
          },
          "sameAs": [
            "https://www.instagram.com/alphavita_hypnose/",
            "https://www.facebook.com/profile.php?id=61563810324076"
          ]
        })}
      </script>
    </Helmet>
  );
}

